// src/data/locations.js
// Market dates, stored as "market_date" metaobjects in Shopify so they can
// be added/edited/confirmed from the admin without a code deploy.

import { shopifyFetch } from '../lib/shopify';

const MARKET_DATES_QUERY = `
  query GetMarketDates {
    metaobjects(type: "market_date", first: 50) {
      edges {
        node {
          id
          fields {
            key
            value
          }
        }
      }
    }
  }
`;

function fieldValue(fields, key) {
    return fields.find(f => f.key === key)?.value ?? null;
}

export async function getMarketDates() {
    const data = await shopifyFetch(MARKET_DATES_QUERY);
    const edges = data?.metaobjects?.edges || [];

    const dates = edges
        .map(({ node }) => {
            const fields = node.fields || [];
            return {
                id: node.id,
                city: fieldValue(fields, 'city'),
                venue: fieldValue(fields, 'venue'),
                startDate: fieldValue(fields, 'start_date'),
                endDate: fieldValue(fields, 'end_date'),
                confirmed: fieldValue(fields, 'confirmed') === 'true',
            };
        })
        // A date with no city or start date isn't usable — skip it rather
        // than show a broken card.
        .filter(d => d.city && d.startDate);

    dates.sort((a, b) => new Date(a.startDate) - new Date(b.startDate));
    return dates;
}

// Dates that haven't finished yet (by end date, falling back to start date),
// soonest first — `dates` must already be sorted ascending.
export function upcoming(dates) {
    const todayStart = new Date();
    todayStart.setHours(0, 0, 0, 0);
    return dates.filter(d => new Date(d.endDate || d.startDate) >= todayStart);
}

// "17 Oct 2026" or "17 – 19 Oct 2026" / "28 Oct – 2 Nov 2026" for ranges.
export function formatDateRange(startDate, endDate) {
    const start = new Date(`${startDate}T00:00:00`);
    const day = (d) => d.getDate();
    const month = (d) => d.toLocaleDateString('en-GB', { month: 'short' });
    const year = (d) => d.getFullYear();

    if (!endDate || endDate === startDate) {
        return `${day(start)} ${month(start)} ${year(start)}`;
    }

    const end = new Date(`${endDate}T00:00:00`);
    if (month(start) === month(end) && year(start) === year(end)) {
        return `${day(start)} – ${day(end)} ${month(end)} ${year(end)}`;
    }
    if (year(start) === year(end)) {
        return `${day(start)} ${month(start)} – ${day(end)} ${month(end)} ${year(end)}`;
    }
    return `${day(start)} ${month(start)} ${year(start)} – ${day(end)} ${month(end)} ${year(end)}`;
}
